import React, { useState, useEffect } from 'react';
import { ProjectCost } from '../types/api';
import { MonthlyAllocation } from '../types/allocation';

interface ProjectCostsProps {
  costs: ProjectCost[];
  months: MonthlyAllocation[];
  onCostsChange: (costs: ProjectCost[]) => void;
}

const ProjectCosts: React.FC<ProjectCostsProps> = ({ costs, months, onCostsChange }) => {
  // Initialize with costs prop, or empty array if not provided
  const [localCosts, setLocalCosts] = useState<ProjectCost[]>(costs || []);

  // Predefined cost categories
  const defaultCategories = [
    { id: 'travel', description: 'Travel', billable: false },
    { id: 'software-license', description: 'Software License', billable: true },
    { id: 'hardware', description: 'Hardware', billable: true },
    { id: 'training', description: 'Training', billable: false },
    { id: 'server', description: 'Server/Cloud', billable: true },
    { id: 'other', description: 'Other', billable: false },
  ];

  // Sync localCosts with costs prop when it changes (e.g., when loading saved dashboard)
  useEffect(() => {
    if (months.length === 0) {
      return; // Don't process if months aren't ready yet
    }

    // If costs prop has data, merge with default categories to ensure all are visible
    if (costs && costs.length > 0) {
      // Create a map of existing costs by ID for quick lookup
      const costsMap = new Map(costs.map(cost => [cost.id, cost]));
      
      // Merge saved costs with default categories
      // This ensures all default categories are always visible, even if they have zero values
      const mergedCosts = defaultCategories.map(cat => {
        const savedCost = costsMap.get(cat.id);
        
        if (savedCost) {
          // Use saved cost, but ensure all months are present
          const monthlyCosts = { ...(savedCost.monthlyCosts || {}) };
          months.forEach(month => {
            if (!(month.monthStart in monthlyCosts)) {
              monthlyCosts[month.monthStart] = 0;
            }
          });
          return {
            id: savedCost.id,
            description: savedCost.description || cat.description,
            billable: savedCost.billable !== undefined ? savedCost.billable : cat.billable,
            monthlyCosts
          };
        } else {
          // Use default category with zero values for all months
          return {
            id: cat.id,
            description: cat.description,
            billable: cat.billable,
            monthlyCosts: months.reduce((acc, month) => ({ ...acc, [month.monthStart]: 0 }), {}),
          };
        }
      });
      
      // Also include any custom costs that aren't in default categories
      const customCosts = costs.filter(cost => !defaultCategories.find(cat => cat.id === cost.id));
      customCosts.forEach(customCost => {
        const monthlyCosts = { ...(customCost.monthlyCosts || {}) };
        months.forEach(month => {
          if (!(month.monthStart in monthlyCosts)) {
            monthlyCosts[month.monthStart] = 0;
          }
        });
        mergedCosts.push({
          id: customCost.id,
          description: customCost.description,
          billable: customCost.billable !== undefined ? customCost.billable : false,
          monthlyCosts
        });
      });
      
      console.log('ProjectCosts: Merging saved costs with default categories:', mergedCosts);
      setLocalCosts(mergedCosts);
    } else if (localCosts.length === 0 && (!costs || costs.length === 0)) {
      // Initialize costs with default categories if empty (only if no costs provided)
      const initialCosts = defaultCategories.map(cat => ({
        id: cat.id,
        description: cat.description,
        billable: cat.billable,
        monthlyCosts: months.reduce((acc, month) => ({ ...acc, [month.monthStart]: 0 }), {}),
      }));
      console.log('ProjectCosts: Initializing with default categories');
      setLocalCosts(initialCosts);
      onCostsChange(initialCosts);
    }
  }, [costs, months, onCostsChange]);

  // Update monthly cost for a category
  const handleCostChange = (id: string, monthStart: string, value: number) => {
    const updatedCosts = localCosts.map(cost =>
      cost.id === id
        ? { ...cost, monthlyCosts: { ...cost.monthlyCosts, [monthStart]: value } }
        : cost
    );
    setLocalCosts(updatedCosts);
    onCostsChange(updatedCosts);
  };

  // Toggle billable status
  const handleBillableChange = (id: string, billable: boolean) => {
    const updatedCosts = localCosts.map(cost =>
      cost.id === id ? { ...cost, billable } : cost
    );
    setLocalCosts(updatedCosts);
    onCostsChange(updatedCosts);
  };

  // Calculate row total
  const calculateRowTotal = (cost: ProjectCost): number => {
    return Object.values(cost.monthlyCosts).reduce((sum, val) => sum + (val || 0), 0);
  };

  // Calculate column total
  const calculateColumnTotal = (monthStart: string): number => {
    return localCosts.reduce((sum, cost) => sum + (cost.monthlyCosts[monthStart] || 0), 0);
  };

  // Calculate grand total
  const calculateGrandTotal = (): number => {
    return localCosts.reduce((sum, cost) => sum + calculateRowTotal(cost), 0);
  };

  // Calculate billable total
  const calculateBillableTotal = (): number => {
    return localCosts
      .filter(cost => cost.billable)
      .reduce((sum, cost) => sum + calculateRowTotal(cost), 0);
  };

  // Calculate non-billable total
  const calculateNonBillableTotal = (): number => {
    return localCosts
      .filter(cost => !cost.billable)
      .reduce((sum, cost) => sum + calculateRowTotal(cost), 0);
  };

  if (months.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-gray-200/50">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Project Costs</h3>
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">Please select a project first to add project costs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-gray-200/50">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">Project Costs</h3>
        <p className="text-sm text-gray-500 mt-1">Enter monthly costs for each category</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                Description
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Billable
              </th>
              {months.map((month) => (
                <th
                  key={month.monthStart}
                  className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  <div>{month.label}</div>
                </th>
              ))}
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {localCosts.map((cost) => {
              const rowTotal = calculateRowTotal(cost);
              return (
                <tr key={cost.id} className="hover:bg-gray-50">
                  {/* Description */}
                  <td className="px-3 py-4 whitespace-nowrap sticky left-0 bg-white z-10">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{cost.description}</span>
                    </div>
                  </td>

                  {/* Billable Toggle */}
                  <td className="px-3 py-4 whitespace-nowrap text-center">
                    <input
                      type="checkbox"
                      checked={cost.billable}
                      onChange={(e) => handleBillableChange(cost.id, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                      title={cost.billable ? 'Billable' : 'Non-billable'}
                    />
                  </td>

                  {/* Monthly Cost Inputs */}
                  {months.map((month) => (
                    <td key={month.monthStart} className="px-2 py-4 whitespace-nowrap">
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={cost.monthlyCosts[month.monthStart] || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          handleCostChange(cost.id, month.monthStart, value);
                        }}
                        className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                        placeholder="0"
                      />
                    </td>
                  ))}

                  {/* Row Total */}
                  <td className="px-3 py-4 whitespace-nowrap text-right bg-blue-50">
                    <span className="text-sm font-semibold text-gray-900">
                      ₹{rowTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </td>
                </tr>
              );
            })}

            {/* Column Totals Row */}
            <tr className="bg-gray-100 font-semibold">
              <td className="px-3 py-3 text-sm text-gray-900 sticky left-0 bg-gray-100 z-10">
                Monthly Total
              </td>
              <td className="px-3 py-3"></td>
              {months.map((month) => {
                const columnTotal = calculateColumnTotal(month.monthStart);
                return (
                  <td key={month.monthStart} className="px-3 py-3 text-right">
                    <span className="text-sm text-gray-900">
                      ₹{columnTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </td>
                );
              })}
              <td className="px-3 py-3 text-right bg-blue-100">
                <span className="text-sm text-gray-900">
                  ₹{calculateGrandTotal().toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Total Project Costs:</span>
            <span className="ml-2 text-gray-900 font-semibold">
              ₹{calculateGrandTotal().toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Billable:</span>
            <span className="ml-2 text-green-600 font-semibold">
              ₹{calculateBillableTotal().toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Non-billable:</span>
            <span className="ml-2 text-red-600 font-semibold">
              ₹{calculateNonBillableTotal().toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCosts;
